import { ActiveCamera, CameraLatestImage } from 'types/CarTracking';

export type PropertyViewMarker = {
    id: number;
    latitude: number;
    longitude: number;
    label: string;
    latestImage: CameraLatestImage | null;
};

export function toFiniteNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeCoordinates(coordinates: [number, number]): { latitude: number; longitude: number } | null {
    const first = toFiniteNumber(coordinates[0]);
    const second = toFiniteNumber(coordinates[1]);
    if (first === null || second === null) {
        return null;
    }

    // New camera payloads are [latitude, longitude].
    if (Math.abs(first) <= 90) {
        return {
            latitude: first,
            longitude: second,
        };
    }

    // Backward compatibility for [longitude, latitude] tuples.
    if (Math.abs(first) <= 180 && Math.abs(second) <= 90) {
        return {
            latitude: second,
            longitude: first,
        };
    }

    return null;
}

export function toPropertyViewMarker(camera: ActiveCamera): PropertyViewMarker | null {
    if (!Array.isArray(camera.coordinates) || camera.coordinates.length < 2) {
        return null;
    }

    const normalizedCoordinates = normalizeCoordinates(camera.coordinates as [number, number]);
    if (!normalizedCoordinates) {
        return null;
    }

    const label = [camera.notes, camera.orientation].filter(Boolean).join(' - ') || `Camera ${camera.id}`;

    const latestImage = camera.latest_image
        ? {
              ...camera.latest_image,
              thumbnail_url: camera.latest_image.thumbnail_url || camera.latest_image.image_url,
          }
        : null;

    return {
        id: camera.id,
        latitude: normalizedCoordinates.latitude,
        longitude: normalizedCoordinates.longitude,
        label,
        latestImage,
    };
}
